import { useEffect, useRef, useState } from "react";
import { useInView } from "../../../hooks/useInView";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

interface CounterProps {
  target: number;
  suffix: string;
  active: boolean;
}

const Counter = ({ target, suffix, active }: CounterProps) => {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!active || started.current) return;
    started.current = true;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [active, target]);

  return (
    <span>
      {count.toLocaleString("tr-TR")}
      {suffix}
    </span>
  );
};

const AboutSection = () => {
  const { ref, inView } = useInView({ threshold: 0.2 });
  const { content } = useSiteContent();
  const section = content.home.about;

  return (
    <section id="about" className="bg-zinc-900 py-20 md:py-28" {...editableAttrs("home.about", "object")}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div
            ref={ref}
            className="relative"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateX(0)" : "translateX(-40px)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
            }}
          >
            <div className="relative">
              <img src={section.imageSrc} alt={section.imageAlt} className="w-full h-[480px] object-cover object-top" {...editableAttrs("home.about.imageSrc", "image")} />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/40 to-transparent"></div>
              <div className="absolute -bottom-3 -right-3 w-24 h-24 border-b-4 border-r-4 border-red-600"></div>
              <div className="absolute -top-3 -left-3 w-24 h-24 border-t-4 border-l-4 border-red-600"></div>
            </div>

            <div className="absolute bottom-6 left-6 bg-red-600 px-5 py-3" {...editableAttrs("home.about.floatingBadgeValue", "text")}>
              <div className="text-white text-2xl font-bold" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                {section.floatingBadgeValue}
              </div>
              <div className="text-white/80 text-xs" {...editableAttrs("home.about.floatingBadgeLabel", "text")}>
                {section.floatingBadgeLabel}
              </div>
            </div>
          </div>

          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s",
            }}
          >
            <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("home.about.badge", "text")}>
              {section.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6 section-title-line" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              <span {...editableAttrs("home.about.titleLines.0", "text")}>{section.titleLines[0]}</span>
              <br />
              <span className="text-red-500" {...editableAttrs("home.about.titleLines.1", "text")}>
                {section.titleLines[1]}
              </span>
              <br />
              <span {...editableAttrs("home.about.titleLines.2", "text")}>{section.titleLines[2]}</span>
            </h2>

            <p className="text-zinc-300 text-base leading-relaxed mb-4" {...editableAttrs("home.about.description", "textarea")}>
              {section.description}
            </p>
            <p className="text-zinc-400 text-base leading-relaxed mb-8" {...editableAttrs("home.about.subDescription", "textarea")}>
              {section.subDescription}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10" {...editableAttrs("home.about.features", "array")}>
              {section.features.map((feat, index) => (
                <div key={`${feat}-${index}`} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-red-500 text-base"></i>
                  </div>
                  <span className="text-zinc-300 text-sm">{feat}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 border-t border-zinc-700 pt-8" {...editableAttrs("home.about.stats", "array")}>
              {section.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                    <i className={`${stat.icon} text-red-500 text-xl`}></i>
                  </div>
                  <div className="text-2xl font-bold text-white" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                    <Counter target={stat.value} suffix={stat.suffix} active={inView} />
                  </div>
                  <div className="text-zinc-500 text-xs mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
